import { useState, useEffect, useCallback } from 'react';
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    deleteDoc,
    updateDoc,
    addDoc,
    getDoc
} from "firebase/firestore";
import { db } from '../firebaseInit';

/**
 * Custom hook for managing broker data and operations
 * @param {string} accountId - The user's account ID
 * @returns {Object} Object containing brokers data and CRUD operations
 */
export const useBrokerStore = (accountId) => {
    const [brokers, setBrokers] = useState([]);
    const [currentBroker, setCurrentBroker] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cacheLastInvalidatedAt, setCacheLastInvalidatedAt] = useState(Date.now());


    /**
     * Fetches brokers from Firestore
     */
    const fetchBrokers = useCallback(async () => {
        if (!accountId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const q = query(collection(db, "brokers"), where("userId", "==", accountId));
            const querySnapshot = await getDocs(q);

            const brokersList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setBrokers(brokersList);
        } catch (err) {
            console.error("Error fetching brokers: ", err);
            setError(err.message || "Failed to fetch brokers");
        } finally {
            setLoading(false);
        }
    }, [accountId]);

    useEffect(() => {
        fetchBrokers();
    }, [fetchBrokers, cacheLastInvalidatedAt]);

    /**
     * Adds a new broker to the database
     * @param {Object} brokerData - The broker data to add
     * @param {string} brokerData.name - Broker name
     * @param {string} brokerData.currency - Broker currency
     * @param {number} brokerData.currentNavInBrokerCurrencyInCents - Current NAV in cents
     */
    const addBroker = async ({ name, currency, currentNavInBrokerCurrencyInCents }) => {
        if (!accountId) {
            throw new Error("Account ID is required to add a broker");
        }

        setError(null);
        
        try {
            await addDoc(collection(db, "brokers"), {
                name,
                currency,
                currentNavInBrokerCurrencyInCents,
                userId: accountId,
                createdAt: new Date().toISOString(),
            });
            setCacheLastInvalidatedAt(Date.now());
        } catch (err) {
            console.error("Error adding broker: ", err);
            setError(err.message || "Failed to add broker");
            throw err;
        }
    };

    /**
     * Selects a broker as the current broker
     * @param {Object|null} broker - The broker to select, or null to deselect
     */
    const selectBroker = (broker) => {
        setCurrentBroker(broker);
    };

    /**
     * Updates an existing broker
     * @param {Object} brokerData - The broker data to update
     * @param {string} brokerData.id - Broker ID
     * @param {string} brokerData.name - Broker name
     * @param {string} brokerData.currency - Broker currency
     * @param {number} brokerData.currentNavInBrokerCurrencyInCents - Current NAV in cents
     */
    const updateBroker = async ({ id, name, currency, currentNavInBrokerCurrencyInCents }) => {
        if (!id) {
            throw new Error("Broker ID is required for update");
        }

        setError(null);
        
        try {
            const ref = doc(db, "brokers", id);

            // Check if the document exists in the database
            const docSnap = await getDoc(ref);
            if (!docSnap.exists()) {
                throw new Error("Broker not found");
            }

            await updateDoc(ref, {
                name,
                currency,
                currentNavInBrokerCurrencyInCents,
                updatedAt: new Date().toISOString(),
            });
            setCacheLastInvalidatedAt(Date.now());
        } catch (err) {
            console.error("Error updating broker: ", err);
            setError(err.message || "Failed to update broker");
            throw err;
        }
    };

    /**
     * Deletes the currently selected broker
     */
    const deleteCurrentBroker = async () => {
        if (!currentBroker) {
            throw new Error("No broker selected for deletion");
        }

        setError(null);
        
        try {
            const ref = doc(db, "brokers", currentBroker.id);
            await deleteDoc(ref);
            setCurrentBroker(null);
            setCacheLastInvalidatedAt(Date.now());
        } catch (err) {
            console.error("Error deleting broker: ", err);
            setError(err.message || "Failed to delete broker");
            throw err;
        }
    };

    /**
     * Clears any existing error state
     */
    const clearError = () => {
        setError(null);
    };

    return { 
        // Data
        brokers, 
        currentBroker, 
        loading, 
        error,
        
        // Actions
        addBroker, 
        selectBroker, 
        updateBroker, 
        deleteCurrentBroker,
        clearError,
        
        // Utilities
        refetch: fetchBrokers
    };
};

